import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminTenantSettingsAPI, adminConnectAPI } from '../services/adminApi';
import { publicAssetUrl } from '../utils/publicAssetUrl';

const Container = styled.div`
  padding: 1rem;
  max-width: 720px;
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1.25rem 0;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  ${(p) => p.$fullWidth && 'grid-column: 1 / -1;'}
`;

const LogoPreviewBox = styled.div`
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background-color: #f8fafc;
  background-image:
    linear-gradient(45deg, #e2e8f0 25%, transparent 25%),
    linear-gradient(-45deg, #e2e8f0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e2e8f0 75%),
    linear-gradient(-45deg, transparent 75%, #e2e8f0 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
`;

const LogoPreviewImg = styled.img`
  display: block;
  max-width: 160px;
  max-height: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 2px rgba(4, 54, 99, 0.15);
  }
  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: white;
  &:focus {
    outline: none;
    border-color: #043663;
  }
  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const Actions = styled.div`
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  &.primary {
    background: #043663;
    color: white;
  }
  &.primary:hover:not(:disabled) {
    background: #032a4a;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  margin: 0;
  font-size: 0.875rem;
  ${(p) => (p.$error ? 'color: #d52b1e;' : 'color: #059669;')}
`;

const StripeCard = styled(Card)`
  margin-top: 1.5rem;
`;

const ConnectStripeBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: #635bff;
  color: white;
  &:hover:not(:disabled) {
    background: #5046e5;
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #d1fae5;
  color: #065f46;
  margin-right: 0.5rem;
`;

const COUNTRY_CODES = [
  { code: 'CH', name: 'Switzerland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'AT', name: 'Austria' },
  { code: 'ES', name: 'Spain' },
  { code: 'GB', name: 'United Kingdom' },
  { code: '', name: '— Select —' },
].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

const TIMEZONES = [
  'Europe/Zurich',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Vienna',
  'Europe/Madrid',
  'Europe/London',
];

const SECTION_KEY = 'settings_general';

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminSettingsGeneral() {
  const { adminUser } = useAdminAuth();
  const canEdit = adminUser?.isSuperAdmin || adminUser?.permissions?.[SECTION_KEY]?.edit;

  const queryClient = useQueryClient();
  const [message, setMessage] = useState({ type: '', text: '' });
  const [logoPreviewVersion, setLogoPreviewVersion] = useState(0);
  const [connectLoading, setConnectLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    location: '',
    country_code: '',
    phone: '',
    address: '',
    contact_email: '',
    timezone: '',
    default_currency: '',
    tournament_logo_url: '',
    tournament_brand_primary_color: '',
    tournament_brand_secondary_color: '',
    tournament_brand_text_color: '',
    tournament_brand_button_color: '',
    tournament_default_cta_text: '',
    campaigns_only_for_new_registrations: false,
    countdown_enabled: false,
    countdown_title: '',
    countdown_target_at: '',
  });

  const { data, isLoading, error } = useQuery(
    'adminTenantSettings',
    () => adminTenantSettingsAPI.get(),
    { staleTime: 60000 }
  );

  const tenant = data?.data;

  useEffect(() => {
    if (tenant) {
      setForm({
        name: tenant.name || '',
        location: tenant.location || '',
        country_code: tenant.country_code || '',
        phone: tenant.phone || '',
        address: tenant.address || '',
        contact_email: tenant.contact_email || '',
        timezone: tenant.timezone || '',
        default_currency: tenant.default_currency || '',
        tournament_logo_url: tenant.tournament_logo_url || '',
        tournament_brand_primary_color: tenant.tournament_brand_primary_color || '',
        tournament_brand_secondary_color: tenant.tournament_brand_secondary_color || '',
        tournament_brand_text_color: tenant.tournament_brand_text_color || '',
        tournament_brand_button_color: tenant.tournament_brand_button_color || '',
        tournament_default_cta_text: tenant.tournament_default_cta_text || '',
        campaigns_only_for_new_registrations: tenant.campaigns_only_for_new_registrations === true,
        countdown_enabled: tenant.countdown_enabled === true || tenant.countdown_enabled === 'true',
        countdown_title: tenant.countdown_title || '',
        countdown_target_at: toDatetimeLocalValue(tenant.countdown_target_at),
      });
    }
  }, [tenant]);

  const updateMutation = useMutation(
    (payload) => adminTenantSettingsAPI.update(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminTenantSettings');
        queryClient.invalidateQueries('publicRegistrationCountdown');
        setMessage({ type: 'success', text: 'Settings saved successfully.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      },
      onError: (err) => {
        setMessage({
          type: 'error',
          text: err.response?.data?.message || 'Failed to save settings.',
        });
      },
    }
  );

  const logoUploadMutation = useMutation(
    (file) => adminTenantSettingsAPI.uploadTournamentLogo(file),
    {
      onSuccess: () => {
        setLogoPreviewVersion((v) => v + 1);
        queryClient.invalidateQueries('adminTenantSettings');
        toast.success('Tournament logo saved. Refresh player pages if the old image still appears.');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Logo upload failed.');
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    updateMutation.mutate({
      name: form.name,
      location: form.location,
      phone: form.phone,
      address: form.address,
      contact_email: form.contact_email,
      timezone: form.timezone,
      default_currency: form.default_currency,
      tournament_logo_url: form.tournament_logo_url,
      tournament_brand_primary_color: form.tournament_brand_primary_color,
      tournament_brand_secondary_color: form.tournament_brand_secondary_color,
      tournament_brand_text_color: form.tournament_brand_text_color,
      tournament_brand_button_color: form.tournament_brand_button_color,
      tournament_default_cta_text: form.tournament_default_cta_text,
      campaigns_only_for_new_registrations: !!form.campaigns_only_for_new_registrations,
      countdown_enabled: !!form.countdown_enabled,
      countdown_title: form.countdown_title,
      countdown_target_at: form.countdown_target_at
        ? new Date(form.countdown_target_at).toISOString()
        : null,
    });
  };

  const isCountry = tenant?.type === 'country';
  const isCourtOrOrg = tenant?.type === 'court' || tenant?.type === 'organization';
  const stripeConnected = !!tenant?.stripe_connected_account_id && !tenant?.stripe_connection_disconnected_at;
  const stripeDisconnectedButHadAccount = !!tenant?.stripe_connected_account_id && !!tenant?.stripe_connection_disconnected_at;
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  // Toast and refresh when returning from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam === 'success') {
      toast.success('Stripe account connected successfully.');
      queryClient.invalidateQueries('adminTenantSettings');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (stripeParam === 'refresh') {
      toast('Complete Stripe setup or try again.', { icon: '🔄' });
      queryClient.invalidateQueries('adminTenantSettings');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [queryClient]);

  const handleConnectStripe = async () => {
    setConnectLoading(true);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}?stripe=success`;
      const refreshUrl = `${window.location.origin}${window.location.pathname}?stripe=refresh`;
      const timeoutMs = 20000;
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), timeoutMs);
      });
      const res = await Promise.race([
        adminConnectAPI.createAccountLink({ return_url: returnUrl, refresh_url: refreshUrl }),
        timeoutPromise,
      ]);
      // Admin API interceptor returns response.data, so res is the body { success, url } directly
      const url = res?.url;
      if (res?.success && url) {
        window.location.href = url;
        return;
      }
      toast.error(res?.message || 'Could not create Stripe link');
    } catch (err) {
      if (err?.message === 'TIMEOUT') {
        toast.error('Request took too long. Please try again.');
      } else {
        const msg = err?.response?.data?.message || err?.message || 'Failed to connect Stripe';
        toast.error(msg);
      }
    } finally {
      setConnectLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const res = await adminConnectAPI.getLoginLink();
      if (res?.success && res?.url) {
        window.open(res.url, '_blank');
        return;
      }
      toast.error(res?.message || 'Could not open Stripe Dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to open Stripe Dashboard');
    }
  };

  const handleStripeDisconnect = async () => {
    if (!window.confirm('Disconnect Stripe for this tenant? You can reconnect anytime to the same account (balance and payouts are preserved).')) return;
    setDisconnectLoading(true);
    try {
      const res = await adminConnectAPI.disconnect();
      if (res?.success) {
        toast.success(res.message || 'Stripe disconnected.');
        queryClient.invalidateQueries('adminTenantSettings');
      } else {
        toast.error(res?.message || 'Could not disconnect');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to disconnect');
    } finally {
      setDisconnectLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container>
        <Card>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            Loading tenant settings…
          </div>
        </Card>
      </Container>
    );
  }

  if (error || !tenant) {
    return (
      <Container>
        <Card>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#d52b1e' }}>
            {error?.message || 'Tenant not found.'}
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>General – Tenant profile</Title>
        <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
          Name, location, contact details. For country tenants you can set timezone and default currency.
        </p>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <Field $fullWidth>
              <Label>Tenant name *</Label>
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="e.g. Switzerland Padel League"
                disabled={!canEdit}
              />
            </Field>
            <Field>
              <Label>Country</Label>
              <Select
                name="country_code"
                value={tenant.type === 'court' && tenant.country_tenant_name ? (tenant.country_tenant_slug || tenant.country_tenant_id || '') : form.country_code}
                onChange={handleChange}
                disabled
              >
                <option value="">—</option>
                {tenant.type === 'court' && tenant.country_tenant_name ? (
                  <option value={tenant.country_tenant_slug || tenant.country_tenant_id || ''}>{tenant.country_tenant_name}</option>
                ) : (
                  COUNTRY_CODES.filter((c) => c.code !== '').map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))
                )}
              </Select>
              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Set at tenant creation
              </span>
            </Field>
            <Field>
              <Label>Location (city / region)</Label>
              <Input
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="e.g. Zurich"
                disabled={!canEdit}
              />
            </Field>
            <Field>
              <Label>Phone</Label>
              <Input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="+41 00 000 00 00"
                disabled={!canEdit}
              />
            </Field>
            <Field $fullWidth>
              <Label>Address</Label>
              <Input
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Street, postal code, city"
                disabled={!canEdit}
              />
            </Field>
            <Field $fullWidth>
              <Label>Contact email</Label>
              <Input
                name="contact_email"
                type="email"
                value={form.contact_email}
                onChange={handleChange}
                placeholder="contact@example.com"
                disabled={!canEdit}
              />
            </Field>
            {isCountry && (
              <>
                <Field>
                  <Label>Timezone</Label>
                  <Select
                    name="timezone"
                    value={form.timezone}
                    onChange={handleChange}
                    disabled={!canEdit}
                  >
                    <option value="">— Select —</option>
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Default currency</Label>
                  <Select
                    name="default_currency"
                    value={form.default_currency}
                    onChange={handleChange}
                    disabled={!canEdit}
                  >
                    <option value="">— Select —</option>
                    <option value="CHF">CHF</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </Select>
                </Field>
              </>
            )}
            <Field $fullWidth>
              <Label>Tournament logo URL (global default)</Label>
              <Input
                name="tournament_logo_url"
                value={form.tournament_logo_url}
                onChange={handleChange}
                placeholder="https://..."
                disabled={!canEdit}
              />
            </Field>
            <Field $fullWidth>
              <Label>Upload tournament logo (stored in database)</Label>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.45 }}>
                This image is shown on player league cards and on the season registration poster. Use PNG or WebP with a
                transparent background when possible (JPEG uploads cannot keep transparency). Re-upload after changing format
                so an older JPEG copy is replaced. Season banners are uploaded when you create or edit each season.
              </p>
              <Input
                type="file"
                accept="image/*"
                disabled={!canEdit || logoUploadMutation.isLoading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  logoUploadMutation.mutate(f);
                  e.target.value = '';
                }}
                style={{ padding: '0.35rem 0' }}
              />
              {tenant?.id ? (
                <LogoPreviewBox title="Checkerboard shows true transparency; solid black is baked into the file">
                  <LogoPreviewImg
                    key={logoPreviewVersion}
                    src={`${publicAssetUrl(`/api/public/tenants/${tenant.id}/tournament-logo`)}?v=${logoPreviewVersion}`}
                    alt=""
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </LogoPreviewBox>
              ) : null}
            </Field>
            <Field>
              <Label>Tournament primary color</Label>
              <Input
                name="tournament_brand_primary_color"
                value={form.tournament_brand_primary_color}
                onChange={handleChange}
                placeholder="#043663"
                disabled={!canEdit}
              />
            </Field>
            <Field>
              <Label>Tournament secondary color</Label>
              <Input
                name="tournament_brand_secondary_color"
                value={form.tournament_brand_secondary_color}
                onChange={handleChange}
                placeholder="#d52b1e"
                disabled={!canEdit}
              />
            </Field>
            <Field>
              <Label>Tournament text color</Label>
              <Input
                name="tournament_brand_text_color"
                value={form.tournament_brand_text_color}
                onChange={handleChange}
                placeholder="#111827"
                disabled={!canEdit}
              />
            </Field>
            <Field>
              <Label>Tournament button color</Label>
              <Input
                name="tournament_brand_button_color"
                value={form.tournament_brand_button_color}
                onChange={handleChange}
                placeholder="#059669"
                disabled={!canEdit}
              />
            </Field>
            <Field $fullWidth>
              <Label>Default tournament CTA text</Label>
              <Input
                name="tournament_default_cta_text"
                value={form.tournament_default_cta_text}
                onChange={handleChange}
                placeholder="Register now"
                disabled={!canEdit}
              />
            </Field>
            <Field $fullWidth style={{ gridColumn: '1 / -1' }}>
              <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.campaigns_only_for_new_registrations}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      campaigns_only_for_new_registrations: e.target.checked,
                    }))
                  }
                  disabled={!canEdit}
                  style={{ width: 'auto' }}
                />
                New registrations steer toward seasons-only (legacy cutover)
              </Label>
              <p style={{ color: '#64748b', margin: '0.35rem 0 0 0', fontSize: '0.875rem' }}>
                Sends <code style={{ fontSize: '0.8125rem' }}>campaigns_only_for_new_registrations</code> on player APIs so UI can down-rank orphan phase flows when operators have finished migrating.
              </p>
            </Field>

            <Field $fullWidth style={{ gridColumn: '1 / -1', marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <Title as="h4" style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>
                Registration countdown
              </Title>
              <p style={{ margin: '0 0 0.85rem', fontSize: '0.875rem', color: '#6b7280' }}>
                Shows a live countdown on the public homepage (pinned above the header) and on the player dashboard (above the player name) until the target date. Hide it by turning the switch off or letting the timer expire.
              </p>
              <Label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={!!form.countdown_enabled}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      countdown_enabled: e.target.checked,
                    }))
                  }
                  disabled={!canEdit}
                  style={{ width: 'auto' }}
                />
                Enable countdown banner
              </Label>
            </Field>
            <Field $fullWidth>
              <Label>Countdown title</Label>
              <Input
                name="countdown_title"
                value={form.countdown_title}
                onChange={handleChange}
                placeholder="e.g. Season 2026–2027 registration opens in"
                disabled={!canEdit || !form.countdown_enabled}
                maxLength={160}
              />
            </Field>
            <Field $fullWidth>
              <Label>Opens at (date &amp; time)</Label>
              <Input
                type="datetime-local"
                name="countdown_target_at"
                value={form.countdown_target_at}
                onChange={handleChange}
                disabled={!canEdit || !form.countdown_enabled}
              />
            </Field>
          </FormGrid>
          <Actions>
            {canEdit && (
              <Button type="submit" className="primary" disabled={updateMutation.isLoading}>
                {updateMutation.isLoading ? 'Saving…' : 'Save changes'}
              </Button>
            )}
            {message.text && (
              <Message $error={message.type === 'error'}>{message.text}</Message>
            )}
          </Actions>
        </form>
      </Card>

      {isCourtOrOrg && (
        <StripeCard>
          <Title>Stripe Connect</Title>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
            Connect your Stripe account to receive payments (e.g. registration fees) for your club. You complete onboarding on Stripe; then payments can be routed to your account.
          </p>
          {stripeConnected ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Badge>Connected</Badge>
              <ConnectStripeBtn type="button" onClick={handleStripeDashboard}>
                Open Stripe Dashboard
              </ConnectStripeBtn>
              <ConnectStripeBtn
                type="button"
                onClick={handleStripeDisconnect}
                disabled={disconnectLoading}
                style={{ background: '#6b7280' }}
              >
                {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
              </ConnectStripeBtn>
            </div>
          ) : stripeDisconnectedButHadAccount ? (
            <div>
              <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
                You previously connected. Reconnect to use the same account (balance and payouts are preserved).
              </p>
              <ConnectStripeBtn type="button" onClick={handleConnectStripe} disabled={connectLoading}>
                {connectLoading ? 'Redirecting…' : 'Reconnect'}
              </ConnectStripeBtn>
            </div>
          ) : (
            <ConnectStripeBtn type="button" onClick={handleConnectStripe} disabled={connectLoading}>
              {connectLoading ? 'Redirecting…' : 'Connect Stripe'}
            </ConnectStripeBtn>
          )}
        </StripeCard>
      )}

      {isCountry && (
        <StripeCard>
          <Title>Stripe (platform)</Title>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
            Stripe is configured at platform level for this country tenant. Registration and other payments use the platform Stripe keys. Connect is only for court/club tenants who receive their own payments.
          </p>
        </StripeCard>
      )}
    </Container>
  );
}
